import type { LearnArticle } from '../types/learnTypes'

// Post-listing reads. Three are transcribed from the "Dashboard: Market Pulse"
// section of the Seller Portal dashboard content deck; `what-happens-next` is
// the lifecycle explainer that used to live inline in AfterYouListCard, moved
// here so the card could become a topic list without losing it.
//
// The lifecycle article deliberately reuses the platform's own stage vocabulary
// (Review / Owner onboarding / Agreement signing / Final approval)
// so the article, the status chips and the request stepper all describe the
// same step with the same word. When a stage is renamed or merged in
// `listingRequestLifecycle.ts`, it is renamed or merged here in the same change
// — an explainer describing a step the seller can no longer see on their own
// request is worse than no explainer.
//
// "Ownership check" and "Price agreement" used to be stages of their own here.
// Both are part of Review now, so their text moved into that step rather than
// being dropped: declaring every owner upfront is still the single biggest thing
// a seller controls.
export const afterListingArticles: LearnArticle[] = [
  {
    slug: 'what-happens-next',
    audience: 'after-listing',
    title: 'What happens next',
    lede: 'How your request moves from submitted to approved — and where we’ll need you.',
    icon: 'hourglass',
    blocks: [
      {
        kind: 'steps',
        items: [
          {
            label: 'Stage 1',
            title: 'Review',
            text: 'Our team checks your title deed, property details and price, and verifies every owner you declared against the title deed. If we propose a different price, you’ll accept or decline it on the request — declining withdraws the request. Declaring all owners upfront is what keeps this step short.',
          },
          {
            label: 'Stage 2',
            title: 'Owner onboarding',
            text: 'Once we accept your request, every owner gets an email invite to set up a Sliceal account and verify their identity. You can follow each owner’s progress on the request.',
          },
          {
            label: 'Stage 3',
            title: 'Agreement signing',
            text: 'With every owner verified, each owner e-signs the MOU and then the seller’s agreement, and Sliceal signs last.',
          },
          {
            label: 'Stage 4',
            title: 'Final approval',
            text: 'Our team does a final check and approves your property for setup. It then moves to your Properties tab, where you can follow it to the marketplace.',
          },
        ],
      },
      {
        kind: 'closing',
        text: 'We’ll notify you at every step. Anything counted under Action required is waiting on you — open that request to see what’s missing.',
      },
    ],
  },
  {
    slug: 'dubai-tokenised-market',
    audience: 'after-listing',
    title: 'Dubai’s AED 60 billion tokenised market — you’re already in it',
    lede: 'You’re early to something big.',
    icon: 'chart-bar',
    blocks: [
      {
        kind: 'paragraph',
        text: 'Real estate tokenisation in Dubai isn’t a private experiment — it’s a government-led initiative. The Dubai Land Department projects that tokenised assets will represent around 7% of Dubai’s property market by 2033, an estimated AED 60 billion in value.',
      },
      {
        kind: 'paragraph',
        text: 'That roadmap is backed by real infrastructure that already exists today: tokenised ownership recorded against official DLD title records, licensing and oversight by VARA, and regulated platforms handling verified investors and controlled fund flows.',
      },
      {
        kind: 'bullets',
        intro: 'What this means for you as a seller:',
        items: [
          {
            lead: 'The market you’ve listed in is growing by design, not by chance.',
            text: 'Regulatory support means more investors entering over time — and every new investor expands the audience for property offerings.',
          },
          {
            lead: 'Early tokenised offerings in Dubai consistently attracted more investor demand than available supply.',
            text: 'As a seller already on the platform, you’re positioned on the supply side of that gap.',
          },
          {
            lead: 'Your experience compounds.',
            text: 'Every listing you complete makes the next one faster — your verification, bank details, and track record carry forward.',
          },
        ],
      },
      {
        kind: 'closing',
        text: 'The sellers who understand this market early won’t just participate in its growth. They’ll be the ones who shaped it.',
      },
    ],
  },
  {
    slug: 'how-regulation-protects-you',
    audience: 'after-listing',
    title: 'How regulation protects you at every step',
    lede: 'The safeguards working behind your listing.',
    icon: 'shield',
    blocks: [
      {
        kind: 'paragraph',
        text: 'Every listing on this platform operates inside one of the most structured frameworks for tokenised property anywhere in the world. Here’s what’s actually protecting you, at every stage:',
      },
      {
        kind: 'bullets',
        items: [
          {
            lead: 'Your property’s records stay official.',
            text: 'Tokenised ownership is recorded against Dubai Land Department title records — the same registry that governs every conventional property transaction in the emirate. Nothing about your asset moves into an informal system.',
          },
          {
            lead: 'Your investors are real and verified.',
            text: 'Every participant completes identity and compliance verification before investing. You will never transact with an anonymous party.',
          },
          {
            lead: 'Your money moves through controlled channels.',
            text: 'Proceeds don’t pass hand-to-hand. Settlement runs through regulated fund flows and lands in your verified bank account, with every transaction recorded and traceable.',
          },
          {
            lead: 'The rules are enforced by regulators, not promises.',
            text: 'VARA — Dubai’s dedicated virtual assets regulator — licenses and supervises platform activity. Compliance isn’t a feature we chose; it’s an obligation we operate under.',
          },
        ],
      },
      {
        kind: 'closing',
        text: 'New technology, old-fashioned protections. That’s the combination that makes this market work.',
      },
    ],
  },
  {
    slug: 'sole-owner-co-owned-or-poa',
    audience: 'after-listing',
    title: 'Sole owner, co-owned, or POA — you can list it',
    lede: 'However you hold it, you can list it.',
    icon: 'id-card',
    blocks: [
      {
        kind: 'paragraph',
        text: 'Property ownership is rarely one-size-fits-all — and the platform is built for that reality.',
      },
      {
        // No bold leads in this list: the source copy writes each ownership
        // structure as one flowing sentence rather than a labelled point.
        kind: 'bullets',
        items: [
          {
            text: 'Sole owners list directly, managing everything from their own dashboard with secure UAE PASS access.',
          },
          {
            text: 'Co-owners list jointly. Every owner on the title is verified and part of the process — no owner is bypassed, which protects all of you and keeps the offering legally clean.',
          },
          {
            text: 'Licensed real estate agents can manage the process for owners who prefer professional handling — from submission through to offering, under their RERA credentials.',
          },
          {
            text: 'Authorised representatives (POA) act for owners who can’t manage the listing themselves — common for owners based abroad. The authorisation is verified during onboarding, so investors see a fully legitimate offering.',
          },
        ],
      },
      {
        kind: 'paragraph',
        text: 'Why this matters: if your next property is held differently from your first — jointly with family, through a representative, or managed by your agent — the structure isn’t an obstacle. Verification adapts to how you own; the offering process stays the same.',
      },
      {
        kind: 'closing',
        text: 'The right structure isn’t the one that fits the platform. The platform fits the structure you already have.',
      },
    ],
  },
]
